import { APIConnectorFetch } from '@24i/st-api-connector/fetch';
import { ProgramModel, AllMenus, Page } from '@24i/st-api-connector';
import { replaceLabelsForMenu, replaceLabelsForPage } from 'Utils/offlineBackstageTranslationsUtil';
import { languageToLocale, localeToLanguage } from '@24i/st-api-connector/utils';
import { Device } from 'DeviceUtils';
import {
    AD_PROVIDER_URI,
    BACKSTAGE_APP_ID,
    BACKSTAGE_ID,
    BACKSTAGE_URI,
    DEFAULT_PLAYLIST_PARAMS,
    MCORE_URI,
    SHOWTIMES_END_TIME_OFFSET
} from '../options';
import offlineBackstage from '../../platformAssets/offlineBackstageConfig.json';
import { initializeCache } from './cache';
import { preFetchEPG } from './cache/helpers';

const tzOffset = 7200 * 1000;

export default class Api {
    constructor(onError) {
        this.backstageConnector = new APIConnectorFetch(
            {
                mCore: {
                    baseUrl: MCORE_URI
                },
                backstage: {
                    baseUrl: BACKSTAGE_URI,
                    serviceId: BACKSTAGE_ID,
                    applicationId: BACKSTAGE_APP_ID
                },
                extras: {
                    maxTimeOut: Device.isWeb ? 30000 : 10000
                },
                adSkipping: {
                    baseUrl: AD_PROVIDER_URI
                }
            },
            onError
        );

        this.cache = null;
    }

    async setup(storage) {
        this.cache = await initializeCache(storage);
        preFetchEPG(this);
    }

    setMCoreOrigin(backstageConfig) {
        const baseUrl = backstageConfig.api?.backstage?.externalServices?.MCore?.baseURL;
        if (baseUrl) {
            this.backstageConnector.session.setMCoreOrigin(baseUrl);
        } else {
            console.log('Cannot set base URL was not found in backstage config');
        }
    }

    setConnectorLocale(locale) {
        this.backstageConnector.session.locale = languageToLocale(locale.toUpperCase());
    }

    init(params) {
        const { language } = params;
        params.language = language.toUpperCase() === 'CS' ? 'CZ' : language;
        return this.backstageConnector.session.init(params);
    }

    fetchMCoreConfig(language = this.backstageConnector.session.locale) {
        // Fallback on cz language, this should be fixed on mCore
        const lang = localeToLanguage(language);
        return this.backstageConnector.home.getConfig(lang.toUpperCase());
    }

    fetchBackstageConfig() {
        return this.backstageConnector.menus.fetchConfig(BACKSTAGE_APP_ID).catch((err) => {
            console.log('Failed to get backstage config:');
            console.log(err);
            return offlineBackstage.application;
        });
    }

    fetchWelcomeScreen(screenId) {
        return this.backstageConnector.menus.fetchWelcomeScreen(screenId);
    }

    fetchPackages() {
        return this.backstageConnector.vod.fetchPackages();
    }

    fetchPage(id) {
        return this.backstageConnector.menus.fetchPage(id).catch((err) => {
            console.log(
                `Failed to get backstage page by id ${id}:`,
                this.backstageConnector.session.locale
            );
            console.log(err);
            const lang = this.backstageConnector.session.locale.toLocaleLowerCase();
            if (lang !== 'en' && offlineBackstage.translations[lang]) {
                const pageCopy = JSON.parse(JSON.stringify(offlineBackstage[id]));
                replaceLabelsForPage(pageCopy, offlineBackstage.translations[lang].pagesById);
                return new Page(pageCopy, id);
            }
            return new Page(offlineBackstage[id], id);
        });
    }

    fetchPlaylist(pageItem, playlistParams = DEFAULT_PLAYLIST_PARAMS) {
        try {
            return this.backstageConnector.menus.fetchPlaylistByItem(pageItem, playlistParams);
        } catch {
            return Promise.resolve(null);
        }
    }

    fetchStreamUrl(options) {
        return this.backstageConnector.tv.fetchStreamUrl(options);
    }

    fetchAdSkipping(scheduleId) {
        try {
            return this.backstageConnector.tv.fetchAdSkipping(scheduleId);
        } catch (err) {
            console.log(err);
            return null;
        }
    }

    fetchAllMenus(label = 'MENU_MOBILES') {
        return this.backstageConnector.menus.fetchAllMenus(label).catch((err) => {
            console.log('Failed to get menus from backstage:');
            console.log(err);
            const lang = this.backstageConnector.session.locale.toLocaleLowerCase();
            if (lang !== 'en' && offlineBackstage.translations[lang]) {
                const menuCopy = JSON.parse(JSON.stringify(offlineBackstage.menus));
                replaceLabelsForMenu(menuCopy, offlineBackstage.translations[lang].menusBySlug);
                return new AllMenus(menuCopy, label);
            }
            return new AllMenus(offlineBackstage.menus, label);
        });
    }

    async fetchEpg(params, useCache = true) {
        // TODO: TEMPORARY SOLUTION UNTIL MCORE WILL FIX TIMESTAMPS
        // Timezone offset must be manually added
        const newParams = { ...params };
        newParams.from += tzOffset;
        newParams.to += tzOffset;
        const cache = this.cache.epg.load(newParams);
        if (cache && useCache) {
            return cache;
        }
        const response = await this.backstageConnector.tv.fetchEpg(newParams);
        this.cache.epg.save(response, newParams);
        return response;
    }

    async fetchChannelsBrief(params, useCustomOrder = false, useCache = true) {
        const cachedChannels = this.cache.channelsBrief.load(params.list, useCustomOrder);
        if (cachedChannels && useCache) {
            return cachedChannels;
        }
        const { tv } = this.backstageConnector;
        const newChannels = await tv.fetchChannelsBrief(params, useCustomOrder);
        this.cache.channelsBrief.save(newChannels, params.list, useCustomOrder);
        return newChannels;
    }

    async fetchChannelBrief(params, channelId, useCustomOrder = false, useCache = true) {
        const cachedChannels = this.cache.channelsBrief.load(params.list, useCustomOrder);
        if (cachedChannels && useCache) {
            return cachedChannels.items.find((channel) => channel.channelId === channelId);
        }
        const { tv } = this.backstageConnector;
        const newChannels = await tv.fetchChannelsBrief(params, useCustomOrder);
        this.cache.channelsBrief.save(newChannels, params.list, useCustomOrder);
        return newChannels.items.find((channel) => channel.channelId === channelId);
    }

    async fetchChannels(params, getOnlyLive = false, useCustomOrder = true, useCache = true) {
        let channels;
        if (useCache) {
            channels = this.cache.channels.load(JSON.stringify(params), useCustomOrder);
        }

        if (!channels) {
            channels = await this.backstageConnector.tv.fetchChannels(params, useCustomOrder);
            this.cache.channels.save(channels, JSON.stringify(params), useCustomOrder);
        }

        if (getOnlyLive) {
            channels = {
                ...channels,
                items: channels.items.map((item) => new ProgramModel(item.live))
            };
        }

        return channels;
    }

    fetchSearch(params) {
        return this.backstageConnector.search.fetchSearch(params);
    }

    fetchSearchV3Extended(params) {
        return this.backstageConnector.search.fetchSearchV3Extended(params);
    }

    fetchSeries(id, refreshCache = true) {
        return this.backstageConnector.vod.fetchSerie(id, refreshCache);
    }

    async fetchShowTimes({ title, channelId, endTime }) {
        const params = {
            filter: {
                'channel.id=': channelId,
                'startTime=ge': new Date(endTime).toISOString(),
                'endTime=le': new Date(endTime + SHOWTIMES_END_TIME_OFFSET).toISOString()
            }
        };

        if (title) params.filter['program.title='] = `"${title}"`;
        return this.backstageConnector.tv.fetchEpgWithCustomFilter(params);
    }

    fetchMovie(id, platform, refreshCache = true) {
        return this.backstageConnector.vod.fetchMovie(id, platform, refreshCache);
    }

    fetchProgramDetail(params, refreshCache = true) {
        return this.backstageConnector.tv.fetchProgramDetail(params, refreshCache);
    }

    async fetchRecordingDetail(params, refreshCache = true) {
        const recordingDetailItem = await this.backstageConnector.tv.fetchRecordingDetail(
            params,
            refreshCache
        );
        recordingDetailItem.modelType = 'recording';
        return recordingDetailItem;
    }

    fetchLanguage() {
        return this.backstageConnector.home.getLanguage();
    }

    fetchSubtitleLanguages() {
        return this.backstageConnector.home.getSubtitleLanguages();
    }

    fetchAudioLanguages() {
        return this.backstageConnector.home.getAudioLanguages();
    }

    setLanguages(configLanguage) {
        return this.backstageConnector.home.setLanguage(configLanguage);
    }

    listDevices() {
        return this.backstageConnector.session.listDevices();
    }

    deleteDevice(id) {
        return this.backstageConnector.session.deleteDevice(id);
    }

    getSessionConfig() {
        return this.backstageConnector.session.userConfig;
    }

    async fetchAllRecordings(params) {
        const parseRecordingIds = (recordingItems) =>
            recordingItems.reduce((programIds, currentItem) => {
                // Recording without episodes treated as a SIMPLE
                if (currentItem.modelType === 'recording') {
                    programIds.push(currentItem.getProgramId());
                    return programIds;
                }
                // SIMPLE recording, regardless if in series model treat as single rec
                const seriesItems = currentItem.getEpisodes().map((episode) => {
                    if (episode.data.type === 'SIMPLE') {
                        programIds.push(episode.getProgramId());
                        return null;
                    }
                    // SERIES recordings
                    return episode.getProgramId();
                });

                // Filter out null (SIMPLE) recordings
                const filteredSeriesItems = seriesItems.filter(Boolean);
                if (filteredSeriesItems.length > 0) {
                    programIds.push(filteredSeriesItems);
                }
                return programIds;
            }, []);

        let recordings = null;
        try {
            // TODO: We must add data object to recordingSeries - should be solved in connector?
            recordings = await this.backstageConnector.tv.fetchAllRecordings(params);
            recordings.ids = parseRecordingIds(recordings.items);
            recordings.items = recordings.items.map((item) => {
                item.data =
                    item.modelType === 'recordingSeries'
                        ? item.getMetaData().metaData.detail
                        : item.data;
                return item;
            });
        } catch (error) {
            throw error;
        }

        return recordings;
    }

    fetchRecommended(movieId) {
        return this.backstageConnector.vod.fetchRecommended(movieId);
    }

    validatePin(value) {
        return this.backstageConnector.auth.checkParentalPin(value);
    }

    changePin(oldPin, newPin) {
        return this.backstageConnector.auth.setParentalPin({
            oldPin,
            newPin
        });
    }

    requestPinReset(params) {
        return this.backstageConnector.auth.requestPinReset(params);
    }

    checkPinResetCode(params) {
        return this.backstageConnector.auth.checkPinResetCode(params);
    }

    resetPin(params) {
        return this.backstageConnector.auth.resetPin(params);
    }

    changePassword(currentPassword, password) {
        return this.backstageConnector.auth.changePassword(currentPassword, password);
    }

    /**
     * @param {ResetPasswordParam} params
     * @returns {Promise<LoginResponse>}
     */
    resetPassword(params) {
        return this.backstageConnector.auth.resetPassword(params);
    }

    /**
     * @param {ValidateResetPasswordCodeParam} params
     * @returns {Promise<ResponseBase>}
     */
    checkPasswordResetHash(params) {
        return this.backstageConnector.auth.validateResetPasswordCode(params);
    }

    fetchCustomChannelMapList(deviceId) {
        return this.backstageConnector.tv.fetchCustomChannelMapList(deviceId);
    }

    fetchCustomChannelMap(deviceId) {
        return this.backstageConnector.tv.fetchCustomChannelMap(deviceId);
    }

    setCustomChannelMap(params) {
        return this.backstageConnector.tv.setCustomChannelMap(params);
    }

    getCategories(language) {
        return this.backstageConnector.home.getCategories(language);
    }

    fetchContinueWatching() {
        return this.backstageConnector.tv.fetchContinueWatching();
    }

    /**
     * @param {EventParams} event
     * @returns {Promise<ResponseBase>}
     */
    setEvent(event) {
        return this.backstageConnector.events.setEvent(event);
    }

    async getParentalLocking() {
        const { parentalLocking } = await this.backstageConnector.auth.getParentalLocking();
        return parentalLocking;
    }

    /**
     * @param {ParentalSettingsParams} params
     * @returns {Promise<ResponseBase>}
     */
    setParentalLocking(params) {
        return this.backstageConnector.auth.setParentalLocking(params);
    }

    /**
     * @param {ProfileSettingsParams} params
     * @returns {Promise<ResponseBase>}
     */
    setProfile(params) {
        return this.backstageConnector.auth.setProfile(params);
    }

    async wipeCache() {
        await this.cache.wipeCache();
    }

    fetchStrings(language) {
        return this.backstageConnector.home.getStrings(language);
    }

    getSubscriptionInfo() {
        return this.backstageConnector.auth.getSubscriptionInfo();
    }

    manageSubscription() {
        return this.backstageConnector.auth.manageSubscriptions();
    }
}
